import { client } from '../client';
import { uploadFile } from '../util/upload';
import {
  ACHTransferBatch,
  ACHTransferBatchListResponse,
  ACHTransferBatchRecord,
  ACHTransferBatchRecordListResponse,
  CreateACHTransferBatchRequest,
} from '~/typings/API';
import { convertValues } from '~/util';

export interface AchImportListParams {
  limit?: number;
  startingAfter?: string;
  status?: string;
}

export type AchImportRecordListParams = AchImportListParams;

export class AchImportRepository {
  static async create(request: CreateACHTransferBatchRequest, idempotency?: string) {
    return client
      .post<CreateACHTransferBatchRequest, ACHTransferBatch>('/transfers/ach/batches', request, {
        idempotency,
        allowNumbersInRequest: true,
      })
      .then((response) => convertValues<ACHTransferBatch>(response));
  }

  static async uploadFile(batchId: string, file: File) {
    return uploadFile<{ file: File }, ACHTransferBatch>(`/transfers/ach/batches/${batchId}/file`, { file }).then(
      (response) => convertValues<ACHTransferBatch>(response)
    );
  }

  static async get(request: GetRequestType) {
    return client
      .get<unknown, ACHTransferBatch>(`/transfers/ach/batches/${request.id}`)
      .then((response) => convertValues<ACHTransferBatch>(response));
  }

  static async getAll(query?: Partial<AchImportListParams>) {
    return client
      .get<AchImportListParams, ACHTransferBatchListResponse>('/transfers/ach/batches', { query })
      .then((response) => ({
        batches: Array.isArray(response?.batches)
          ? response.batches.map((e) => convertValues<ACHTransferBatch>(e))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async confirm(batchId: string, idempotency?: string) {
    return client
      .post<unknown, ACHTransferBatch>(`/transfers/ach/batches/${batchId}/confirm`, undefined, { idempotency })
      .then((response) => convertValues<ACHTransferBatch>(response));
  }

  static async cancel(batchId: string, idempotency?: string) {
    return client
      .post<unknown, ACHTransferBatch>(`/transfers/ach/batches/${batchId}/cancel`, undefined, { idempotency })
      .then((response) => convertValues<ACHTransferBatch>(response));
  }

  static async getRecords(batchId: string, query?: Partial<AchImportRecordListParams>) {
    return client
      .get<
        AchImportRecordListParams,
        ACHTransferBatchRecordListResponse
      >(`/transfers/ach/batches/${batchId}/records`, { query })
      .then((response) => ({
        records: Array.isArray(response?.records)
          ? response.records.map((e) => convertValues<ACHTransferBatchRecord>(e))
          : [],
        hasMore: Boolean(response?.hasMore),
      }));
  }

  static async getRecord(request: { batchId: string; recordId: string }) {
    return client
      .get<unknown, ACHTransferBatchRecord>(`/transfers/ach/batches/${request.batchId}/records/${request.recordId}`)
      .then((response) => convertValues<ACHTransferBatchRecord>(response));
  }
}
